/**
 * GL UUID Tracking Service
 *
 * Provides a resilient mechanism to read the GL tracking UUID from:
 * 1. Server-Timing header (on page navigations from Worker)
 * 2. Cookie (primary transport)
 * 3. localStorage (survives ITP/cookie purges)
 *
 * Primary flow: Browser sends gl_tracking_uuid cookie automatically
 * Fallback flow: Read UUID from Server-Timing or fallback storage if cookie unavailable
 */

const STORAGE_KEY = 'gl_uuid'

/**
 * Safely reads/writes to localStorage
 * Handles Safari private mode and other environments where localStorage may throw
 */
function safeLocalStorageGetItem(key: string): string | null {
  if (typeof localStorage === 'undefined') {
    return null
  }

  try {
    return localStorage.getItem(key)
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('[UUID Tracking] localStorage.getItem failed:', e)
    }
    return null
  }
}

function safeLocalStorageSetItem(key: string, value: string): boolean {
  if (typeof localStorage === 'undefined') {
    return false
  }

  try {
    localStorage.setItem(key, value)
    return true
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('[UUID Tracking] localStorage.setItem failed:', e)
    }
    return false
  }
}

function safeLocalStorageRemoveItem(key: string): boolean {
  if (typeof localStorage === 'undefined') {
    return false
  }

  try {
    localStorage.removeItem(key)
    return true
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('[UUID Tracking] localStorage.removeItem failed:', e)
    }
    return false
  }
}

/**
 * Reads a cookie value by name
 *
 * @param name Cookie name
 * @returns Cookie value or null if not found
 */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  try {
    const m = document.cookie.match(
      new RegExp('(?:^|;\\s*)' + name + '=([^;]+)')
    )
    return m ? decodeURIComponent(m[1]) : null
  } catch {
    return null
  }
}

/**
 * Extracts UUID from Server-Timing header via performance API
 * Works because Server-Timing is readable from navigation entries
 * Only available on full page navigations, not SPA route changes
 *
 * @returns UUID string or null if not found
 */
function uuidFromServerTiming(): string | null {
  if (typeof window === 'undefined' || typeof performance === 'undefined') {
    return null
  }

  try {
    const nav = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming & {
      serverTiming?: Array<{ name: string; description?: string }>
    }
    const hit = nav?.serverTiming?.find(
      (e) => e.name === 'uuid' && e.description
    )
    return hit?.description || null
  } catch {
    return null
  }
}

/**
 * UUID Tracking Service
 *
 * Resilient UUID management with multiple fallback layers:
 * 1. Server-Timing header (navigation only)
 * 2. Cookie (primary transport)
 * 3. localStorage (survives cookie purges)
 */
export const UUIDTracking = {
  _uuid: null as string | null,

  /**
   * Initializes UUID tracking on app startup
   * Tries multiple sources in priority order
   * Safely handles localStorage failures (e.g., Safari private mode)
   */
  initialize() {
    // 1) Try server timing (available on doc navigations from the Worker)
    this._uuid = uuidFromServerTiming()

    // 2) Fall back to cookie (if present)
    if (!this._uuid) {
      this._uuid = readCookie('gl_tracking_uuid')
    }

    // 3) Fall back to localStorage to survive ITP purges (safely, with fallback)
    if (!this._uuid) {
      this._uuid = safeLocalStorageGetItem(STORAGE_KEY)
    }

    // Store in localStorage for future resilience (safely, no hard fail)
    if (this._uuid) {
      safeLocalStorageSetItem(STORAGE_KEY, this._uuid)
    }

    if (import.meta.env.DEV && this._uuid) {
      console.log('[UUID Tracking] Initialized:', this._uuid)
    }
  },

  /**
   * Gets the current UUID
   *
   * @returns UUID string or null if unavailable
   */
  get() {
    return this._uuid
  },

  /**
   * Refresh UUID from document sources (call after GLU hops or auth flows)
   * Useful for SPA route changes or when you suspect the UUID has changed
   * Safely handles localStorage failures without hard-failing
   *
   * @returns Updated UUID or null
   */
  refreshFromDocument() {
    const fromTiming = uuidFromServerTiming() || readCookie('gl_tracking_uuid')

    if (fromTiming && fromTiming !== this._uuid) {
      this._uuid = fromTiming
      safeLocalStorageSetItem(STORAGE_KEY, this._uuid)

      if (import.meta.env.DEV) {
        console.log('[UUID Tracking] Refreshed:', this._uuid)
      }
    }

    return this._uuid
  },

  /**
   * Clears the cached UUID (useful for testing)
   * Safely handles localStorage failures without hard-failing
   */
  clearCache() {
    this._uuid = null
    safeLocalStorageRemoveItem(STORAGE_KEY)
  },
}

export default UUIDTracking
